import { format } from "date-fns";
import { useCallback, useState } from "react";

import { Loader } from "@whoosh/design-system/src/components/display";
import {
  ClockIcon,
  PersonFilledIcon,
  PlaceIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import {
  convertDateStringToObj,
  formatAgendaDateTimeHHMM,
  formatHHMM,
  isDateStringValid,
} from "@whoosh/utilities/src/dates";

import { SelectedRow, SlotPricingPreview } from "../types";
import { formatSlotPricingPreview } from "../utils/formatSlotPricingPreview";

type Props = {
  time: string;
  dateTime?: string | null;
  agendaDate?: string | null;
  courseBucketName?: string;
  courseBucketId?: string;
  pricingPreview?: SlotPricingPreview;
  rateRange?: string[];
  variant: "only" | "first" | "last" | "middle";
  maxDuration: number;
  minDuration: number;
  minGroupSize: number;
  maxGroupSize: number;
  capacity: number;
  usedCapacity: number;
  onPress: (row: SelectedRow) => void;
};

export const TimeCard = ({
  time,
  dateTime,
  agendaDate,
  variant,
  courseBucketName,
  courseBucketId,
  pricingPreview,
  rateRange,
  minDuration,
  maxDuration,
  onPress,
  capacity,
  usedCapacity,
  minGroupSize,
}: Props) => {
  const timeDisplay =
    (dateTime ? formatAgendaDateTimeHHMM(dateTime) : null) ??
    formatHHMM(time) ??
    "";
  const slotDateString = dateTime?.split(/[T ]/)[0];
  const dateDisplay =
    isDateStringValid(slotDateString) &&
    isDateStringValid(agendaDate) &&
    slotDateString !== agendaDate
      ? format(convertDateStringToObj(slotDateString), "MMM d")
      : null;
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const { currencySymbol } = useGetClubCurrency();
  const rateDisplay =
    rateRange !== undefined
      ? rateRange.length > 1 && rateRange[0] !== rateRange[1]
        ? rateRange.join(" - ")
        : rateRange[0] ?? null
      : formatSlotPricingPreview(pricingPreview, currencySymbol);
  const durationDisplay = durationDisplayFromMinutes(minDuration, maxDuration);

  const _onPress = useCallback(() => {
    setShouldShowLoader(true);
    onPress({
      time,
      courseBucketId,
      maxDuration,
      loaderCallback: () => setShouldShowLoader(false),
    });
  }, [courseBucketId, maxDuration, onPress, time]);

  const finalCapacity = capacity - usedCapacity;
  const minCapacity = minGroupSize ?? 1;

  return (
    <Pressable
      onPress={_onPress}
      _pressed={{
        backgroundColor: "button-alt-surface-pressed",
      }}
      marginBottom={variant === "last" || variant === "only" ? 2 : 1}
      backgroundColor="surface-primary"
      borderTopLeftRadius={
        variant === "first" || variant === "only" ? "4" : "0"
      }
      borderTopRightRadius={
        variant === "first" || variant === "only" ? "4" : "0"
      }
      borderBottomLeftRadius={
        variant === "last" || variant === "only" ? "4" : "0"
      }
      borderBottomRightRadius={
        variant === "last" || variant === "only" ? "4" : "0"
      }
      shadowColor="black"
      shadowOpacity={0.04}
      shadowOffset={{ width: 0, height: 2 }}
      shadowRadius={4}
    >
      {shouldShowLoader ? <Loader /> : null}
      <HStack p={4} alignItems="center" justifyContent="space-between">
        <VStack gap="1.5">
          <HStack gap={1} alignItems="baseline">
            <Text
              variant="mainBaseSemiBold"
              testID={`duration-based-agenda-time-card-time-${time}`}
            >
              {timeDisplay}
            </Text>
            {dateDisplay ? (
              <Text
                variant="mainSmallMedium"
                color="text-secondary"
                testID={`duration-based-agenda-time-card-date-${time}`}
              >
                {dateDisplay}
              </Text>
            ) : null}
          </HStack>
          {courseBucketName ? (
            <HStack gap={1}>
              <Text variant="mainTiny" color="text-secondary">
                {courseBucketName}
              </Text>
              <PlaceIcon color="text-secondary" />
            </HStack>
          ) : null}
        </VStack>
        <VStack gap="1.5" alignItems="flex-end">
          {rateDisplay ? (
            <Text
              variant="mainBaseSemiBold"
              color="text-success"
              testID={`duration-based-agenda-time-card-rate-${time}`}
            >
              {rateDisplay}
            </Text>
          ) : null}
          <HStack gap={1} alignItems="center">
            <HStack gap={1} alignItems="center">
              <Text
                variant="mainTiny"
                color="text-secondary"
                testID={`duration-based-agenda-time-card-capacity-${time}`}
              >
                {`${minCapacity}-${finalCapacity}`}
              </Text>
              <PersonFilledIcon color="text-secondary" />
            </HStack>
            <Text variant="mainBaseSemiBold" color="text-secondary">
              ∙
            </Text>
            <HStack gap={1}>
              <Text
                variant="mainTiny"
                color="text-secondary"
                testID={`duration-based-agenda-time-card-duration-${time}`}
              >
                {durationDisplay}
              </Text>
              <ClockIcon color="text-secondary" />
            </HStack>
          </HStack>
        </VStack>
      </HStack>
    </Pressable>
  );
};

const durationDisplayFromMinutes = (
  minTotalMinutes: number,
  maxTotalMinutes: number
): string | null => {
  const minHours = Math.floor(minTotalMinutes / 60);
  const minMinutes = minTotalMinutes % 60;

  const maxHours = Math.floor(maxTotalMinutes / 60);
  const maxMinutes = maxTotalMinutes % 60;

  if (minTotalMinutes === maxTotalMinutes) {
    return durationDisplayFromMinutesHours(minMinutes, minHours);
  }

  if (minMinutes === 0 && maxMinutes === 0) {
    return `${minHours} - ${maxHours} hr`;
  }

  if (minHours === 0 && maxHours === 0) {
    return `${minMinutes} - ${maxMinutes} min`;
  }

  return `${durationDisplayFromMinutesHours(
    minMinutes,
    minHours
  )}-${durationDisplayFromMinutesHours(maxMinutes, maxHours)}`;
};

const durationDisplayFromMinutesHours = (minutes: number, hours: number) => {
  return `${hours >= 1 ? hours + (hours > 1 ? " hr" : " hr") : ""}${
    hours >= 1 && minutes ? " " : ""
  }${minutes ? minutes + " min" : hours ? "" : "0 min"}`;
};
